import { useEffect, useRef, useState } from 'react';
import { IconButton, Button } from '@/shared/ui/primitives';
import { Ellipsis } from 'lucide-react';
import { useNavigate } from 'react-router';

import { useDeleteClient } from '../api';
import { DialogBox } from '@/shared/ui';
import { paths } from '@/navigation/paths';
import { TEST_IDS } from '@/testIds';
import styles from './ClientOptions.module.css';

interface ClientOptionsProps {
  clientId: string;
  onRename?: () => void;
  onDeleted?: () => void;
}

export const ClientOptions = ({ clientId, onRename, onDeleted }: ClientOptionsProps) => {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const deleteClientMutation = useDeleteClient({
    onSuccess: () => {
      onDeleted?.();
      setConfirmDelete(false);
      void navigate(paths.home());
    },
  });

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const trigger = wrapperRef.current?.querySelector<HTMLButtonElement>('[aria-haspopup]');
    const menu = wrapperRef.current?.querySelector<HTMLElement>('[role="menu"]');
    menu?.querySelector<HTMLButtonElement>('button')?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!menu?.contains(document.activeElement)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        trigger?.focus();
        return;
      }
      const items = [...menu.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
      const current = items.findIndex((item) => item === document.activeElement);
      const next =
        event.key === 'ArrowDown'
          ? (current + 1) % items.length
          : event.key === 'ArrowUp'
            ? (current - 1 + items.length) % items.length
            : event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? items.length - 1
                : -1;
      if (next >= 0) {
        event.preventDefault();
        items[next]?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleRenameClick = () => {
    setOpen(false);
    wrapperRef.current?.querySelector<HTMLButtonElement>('[aria-haspopup]')?.focus();
    onRename?.();
  };

  const handleDeleteClick = () => {
    setOpen(false);
    wrapperRef.current?.querySelector<HTMLButtonElement>('[aria-haspopup]')?.focus();
    setConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    if (deleteClientMutation.isPending) return;
    deleteClientMutation.mutate({ id: clientId });
  };

  return (
    <>
      <div className={styles.wrapper} ref={wrapperRef}>
        <IconButton
          size="md"
          variant="surface"
          onClick={() => setOpen(!open)}
          data-testid={TEST_IDS.clientOptionsTrigger}
          aria-label="Client options"
          aria-expanded={open}
          aria-haspopup="menu"
        >
          <Ellipsis size={24} aria-hidden="true" />
        </IconButton>
        {open && (
          <div className={styles.menu} role="menu" aria-label="Client actions">
            {onRename && (
              <Button
                variant="ghost"
                role="menuitem"
                onClick={handleRenameClick}
                data-testid={TEST_IDS.renameClientMenuItem}
              >
                Rename
              </Button>
            )}
            <Button
              variant="ghost"
              role="menuitem"
              onClick={handleDeleteClick}
              disabled={deleteClientMutation.isPending}
              className={styles.deleteAction}
              data-testid={TEST_IDS.deleteClientMenuItem}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      <DialogBox
        isOpen={confirmDelete}
        message="Are you sure you want to delete this client? This action cannot be undone."
        confirmText="Delete"
        isDestructive
        isLoading={deleteClientMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};
